import React, { useEffect, useRef, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { refreshDewarCustomers, getRefreshDewarCustomersStatus } from '../../../services/requests';

// Stable id so the progress toast dedupes across page navigation/remounts.
const DEWAR_REFRESH_TOAST_ID = 'dewar-refresh';

const formatProgress = (status): string => {
  const total = status?.total ?? 0;
  const processed = status?.processed ?? 0;
  if (total > 0) {
    const pct = Math.min(100, Math.floor((processed / total) * 100));
    return `Refreshing dewar customers… ${processed}/${total} (${pct}%)`;
  }
  return 'Refreshing dewar customers…';
};

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_FAILURES = 3;
const LOST_TRACK_MESSAGE =
  'Lost track of the dewar customer refresh. Verify the customer data and run the refresh again if needed.';
const POLL_FAILING_MESSAGE =
  'Unable to check dewar refresh status. It may still be running on the server; retrying…';

const formatSuccess = (status): string => {
  const inserted = status?.inserted ?? 0;
  const updated = status?.updated ?? 0;
  return `Dewar customers refreshed: ${inserted} added, ${updated} updated.`;
};

const percentOf = (status): number | null => {
  const total = status?.total ?? 0;
  if (total <= 0) return null;
  return Math.min(100, Math.floor(((status?.processed ?? 0) / total) * 100));
};

// Remembers which run this browser started, so only the requester gets toasts (even after leaving the page).
const OWNED_RUN_KEY = 'dewarCustomerRefreshRunId';
const getOwnedRunId = (): number | null => {
  const id = Number(localStorage.getItem(OWNED_RUN_KEY));
  return Number.isFinite(id) && id > 0 ? id : null;
};
const setOwnedRunId = (id: number | null) => {
  if (id) localStorage.setItem(OWNED_RUN_KEY, String(id));
  else localStorage.removeItem(OWNED_RUN_KEY);
};

// The refresh runs as a server background job; its status is persisted, so it survives
// leaving the page and server restarts (a dead job is reported as an error, not success).
export const DewarRefreshButton = ({ loadData }: { loadData?: () => void }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [checking, setChecking] = useState(true);
  const [runStatus, setRunStatus] = useState<any>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeRef = useRef(false);
  const mountedRef = useRef(true);
  const runIdRef = useRef<number | null>(null);
  // True when this browser started the tracked run; only then are toasts shown.
  const ownerRef = useRef(false);
  const pollFailuresRef = useRef(0);
  const loadDataRef = useRef(loadData);
  loadDataRef.current = loadData;

  const clearPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const begin = (runId: number | null, owner: boolean) => {
    activeRef.current = true;
    runIdRef.current = runId;
    ownerRef.current = owner;
    pollFailuresRef.current = 0;
    setIsRunning(true);
  };

  const finish = (success: boolean, message?: string) => {
    if (!activeRef.current) return;
    activeRef.current = false;
    clearPoll();
    if (mountedRef.current) {
      setIsRunning(false);
      setRunStatus(null);
    }
    if (!ownerRef.current) return;
    setOwnedRunId(null);
    if (success) {
      toast.success(message || 'Dewar customers successfully refreshed!', { id: DEWAR_REFRESH_TOAST_ID });
      if (mountedRef.current && typeof loadDataRef.current === 'function') {
        loadDataRef.current();
      }
    } else {
      toast.error(message || 'Dewar customer refresh failed.', { id: DEWAR_REFRESH_TOAST_ID });
    }
  };

  const applyStatus = (status) => {
    if (!status) {
      finish(false, LOST_TRACK_MESSAGE);
      return;
    }
    if (status.isRunning) {
      if (mountedRef.current) setRunStatus(status);
      if (ownerRef.current) toast.loading(formatProgress(status), { id: DEWAR_REFRESH_TOAST_ID });
      return;
    }
    if (status.error) {
      finish(false, status.error);
      return;
    }
    finish(true, formatSuccess(status));
  };

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const status = await getRefreshDewarCustomersStatus(runIdRef.current);
        pollFailuresRef.current = 0;
        applyStatus(status);
      } catch {
        pollFailuresRef.current += 1;
        if (ownerRef.current && pollFailuresRef.current >= MAX_POLL_FAILURES) {
          toast.error(POLL_FAILING_MESSAGE, { id: DEWAR_REFRESH_TOAST_ID, duration: Infinity });
        }
      }
    }, POLL_INTERVAL_MS);
  };

  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      try {
        const status = await getRefreshDewarCustomersStatus();
        if (!mountedRef.current) return;
        const ownedRunId = getOwnedRunId();
        const ownsActiveRun = !!status?.isRunning && status.runId === ownedRunId;
        if (status?.isRunning) {
          begin(status.runId ?? null, ownsActiveRun);
          setRunStatus(status);
          if (ownsActiveRun) toast.loading(formatProgress(status), { id: DEWAR_REFRESH_TOAST_ID });
          startPolling();
        } else {
          // Clear any stale progress toast left over from a run that finished while away.
          toast.dismiss(DEWAR_REFRESH_TOAST_ID);
        }
        // The requester's run finished while they were away: report its outcome once.
        if (ownedRunId && !ownsActiveRun) {
          setOwnedRunId(null);
          const owned = status?.runId === ownedRunId ? status : await getRefreshDewarCustomersStatus(ownedRunId);
          if (owned && !owned.isRunning) {
            if (owned.error) toast.error(owned.error, { id: DEWAR_REFRESH_TOAST_ID });
            else toast.success(formatSuccess(owned), { id: DEWAR_REFRESH_TOAST_ID });
          }
        }
      } catch {
        // Ignore; leave the button enabled if status can't be read.
      } finally {
        if (mountedRef.current) setChecking(false);
      }
    })();
    return () => {
      mountedRef.current = false;
      clearPoll();
      // Loading toasts never expire, so without polling it would freeze; returning to the page resumes it.
      if (activeRef.current && ownerRef.current) toast.dismiss(DEWAR_REFRESH_TOAST_ID);
    };
  }, []);

  const onClick = async () => {
    if (isRunning) return;
    if (confirm('Are you sure you want to refresh dewar customers?') !== true) return;

    begin(null, true);
    toast.loading('Refreshing dewar customers…', { id: DEWAR_REFRESH_TOAST_ID });

    let runId: number | null = null;
    try {
      const started = await refreshDewarCustomers();
      runId = started?.runId ?? null;
      setOwnedRunId(runId);
    } catch (err: any) {
      const message = typeof err === 'string' ? err : (err?.message || '');
      // 409: someone else's refresh is running — show it on the button only.
      try {
        const status = await getRefreshDewarCustomersStatus();
        if (status?.isRunning) {
          runId = status.runId ?? null;
          ownerRef.current = false;
          toast.dismiss(DEWAR_REFRESH_TOAST_ID);
          if (mountedRef.current) setRunStatus(status);
        }
      } catch {
        // Fall through and report the original failure.
      }
      if (runId === null) {
        finish(false, message || undefined);
        return;
      }
    }

    if (!mountedRef.current) {
      activeRef.current = false;
      return;
    }
    runIdRef.current = runId;
    startPolling();
  };

  const pct = percentOf(runStatus);
  const label = isRunning ? `Refreshing…${pct !== null ? ` ${pct}%` : ''}` : 'Refresh Customers...';
  const title = isRunning && runStatus?.requestedBy ? `Refresh started by ${runStatus.requestedBy}` : undefined;

  // Wrapper carries the tooltip because disabled buttons don't receive hover events.
  return (
    <span className="d-inline-block" title={title}>
      <Button variant="primary" size="sm" onClick={onClick} disabled={isRunning || checking}>
        {label}
        {isRunning && <>{' '}<Spinner animation="border" size="sm" /></>}
      </Button>
    </span>
  );
};
